/**
 * `PrioritiesScene` projections — the driver tree laid out as an icicle, the
 * headline measures, the "target first" rows and the calculation drawer.
 *
 * Pure: no React, no engine mutation, no clock. The reliability engine does
 * the arithmetic (`driverTree`, `priorities`); this file only arranges it for
 * one page.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/fleet/PrioritiesScene.tsx
 * @see docs/logic/fleet/priorities.md
 */

import type { SourceTablePayload } from '../../chrome-oem/SourceDrawerBody';
import { BY_ID } from '../../data/core';
import type { MineCode } from '../../schema/machine';
import type { DriverKind, DriverNode, RootCause, SiteRole } from '../../schema/operations';
import { coi, fmt$, fmtk, MSHORT } from '../../shared/oem-engine';
import {
  DRIVER_LABEL,
  driverTree,
  priorities,
  unplannedCost,
  type DriverTreeCtx,
  type Priority,
} from '../../shared/reliability';
import { ALERTS } from '../../shared/seed/alerts';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Driver kinds in legend order — the order the engine's weights are listed. */
export const DRIVER_ORDER: readonly DriverKind[] = ['condition', 'drift', 'operator', 'late-pm', 'parts'];

/** Driver kind → its colour token. One hue each, resolved per theme. */
export const DRIVER_COLOR: Readonly<Record<DriverKind, string>> = {
  condition: 'var(--crit)',
  drift: 'var(--warn)',
  operator: 'var(--info)',
  'late-pm': 'var(--band-major)',
  parts: 'var(--ink-tertiary)',
};

/** The three roles that own operator behaviour. Everything else is maintenance. */
const OPERATIONS_ROLES: ReadonlySet<SiteRole> = new Set<SiteRole>([
  'Operating Supervisor',
  'Superintendent',
  'Mine Control',
]);

/** Root cause → the pill's words and colour token. */
export const CAUSE_PILL: Readonly<Record<RootCause, { label: string; color: string; hint: string }>> = {
  equipment: {
    label: 'Equipment',
    color: 'var(--band-poor)',
    hint: 'The machine, not the operator — to the reliability engineer and planner',
  },
  operator: {
    label: 'Operator',
    color: 'var(--info)',
    hint: 'Driving behaviour — to the operating supervisor and mine control',
  },
  mixed: {
    label: 'Mixed',
    color: 'var(--warn)',
    hint: 'Both — one operator carries most events and the component is wearing',
  },
  insufficient: {
    label: 'Unclear',
    color: 'var(--ink-tertiary)',
    hint: 'Too few events to call — triage',
  },
};

// ---------------------------------------------------------------------------
// The page's data, once
// ---------------------------------------------------------------------------

/** Everything the page renders, derived once per context. */
export interface PrioritiesView {
  tree: DriverNode;
  /** Every machine with exposure, ranked. */
  rows: readonly Priority[];
}

/** Build the tree and the full ranking. Call inside a `useMemo`. */
export function prioritiesView(ctx: DriverTreeCtx): PrioritiesView {
  return { tree: driverTree(ctx), rows: priorities(ctx, Number.POSITIVE_INFINITY) };
}

// ---------------------------------------------------------------------------
// Headline
// ---------------------------------------------------------------------------

export interface DriverTotal {
  kind: DriverKind;
  label: string;
  valueAud: number;
  /** Share of the fleet total, 0–1. */
  share: number;
}

/** A$ by driver kind across the fleet, largest first. Zero kinds are dropped. */
export function driverTotals(tree: DriverNode): DriverTotal[] {
  const sums = new Map<DriverKind, number>();
  for (const mine of tree.children)
    for (const machine of mine.children)
      for (const d of machine.children)
        if (d.driverKind) sums.set(d.driverKind, (sums.get(d.driverKind) ?? 0) + d.valueAud);
  return DRIVER_ORDER.filter((k) => (sums.get(k) ?? 0) > 0)
    .map((kind) => {
      const valueAud = sums.get(kind) ?? 0;
      return { kind, label: DRIVER_LABEL[kind], valueAud, share: tree.valueAud ? valueAud / tree.valueAud : 0 };
    })
    .sort((a, b) => b.valueAud - a.valueAud);
}

export interface PrioritiesHeadline {
  totalLabel: string;
  totalExact: string;
  machines: number;
  alerts: number;
  flagged: number;
  /** Fewest machines whose exposure reaches 80 % of the total. */
  paretoCount: number;
  /** The share those machines actually carry, 0–1. */
  paretoShare: number;
  topMachine: { machine: string; component: string; share: number } | null;
  topDriver: DriverTotal | null;
  nextDrivers: readonly DriverTotal[];
  /** Machines whose routing reaches an operations role. */
  toOperations: number;
  /** Machines whose routing reaches a maintenance role. */
  toMaintenance: number;
  /** The machines that go to operations, for the detail line. */
  operationsMachines: readonly string[];
}

/** The four headline measures. */
export function prioritiesHeadline(view: PrioritiesView): PrioritiesHeadline {
  const { tree, rows } = view;
  const total = tree.valueAud;
  let running = 0;
  let paretoCount = 0;
  for (const r of rows) {
    if (running >= 0.8 * total) break;
    running += r.valueAud;
    paretoCount += 1;
  }
  const drivers = driverTotals(tree);
  const top = rows[0];
  const reaches = (r: Priority, ops: boolean) =>
    r.decision.routes.some((t) => OPERATIONS_ROLES.has(t.role) === ops);
  const opsRows = rows.filter((r) => reaches(r, true));
  return {
    totalLabel: fmtk(total),
    totalExact: fmt$(total),
    machines: rows.length,
    alerts: rows.filter((r) => r.basis === 'alert').length,
    flagged: rows.filter((r) => r.basis === 'risk').length,
    paretoCount,
    paretoShare: total ? running / total : 0,
    topMachine: top ? { machine: top.machine, component: top.component, share: total ? top.valueAud / total : 0 } : null,
    topDriver: drivers[0] ?? null,
    nextDrivers: drivers.slice(1, 3),
    toOperations: opsRows.length,
    toMaintenance: rows.filter((r) => reaches(r, false)).length,
    operationsMachines: opsRows.map((r) => r.machine),
  };
}

/** `0.6012` → `'60%'`; a non-zero share under half a percent → `'<1%'`. */
export const pct = (share: number): string =>
  share > 0 && share < 0.005 ? '<1%' : `${Math.round(share * 100)}%`;

// ---------------------------------------------------------------------------
// The icicle
// ---------------------------------------------------------------------------

/** Column index: 0 total, 1 mine, 2 machine, 3 driver. */
export type IcicleColumn = 0 | 1 | 2 | 3;

export interface IcicleCell {
  id: string;
  column: IcicleColumn;
  kind: DriverNode['kind'];
  /** Short label for the block. */
  label: string;
  /** Second line, when there is room — the component, or the site name. */
  sublabel?: string;
  valueAud: number;
  valueLabel: string;
  /** Share of the fleet total, 0–1. */
  shareOfTotal: number;
  /** Top edge and height as fractions of the chart height. */
  y: number;
  h: number;
  mine?: MineCode;
  machine?: string;
  driverKind?: DriverKind;
  /** The breadcrumb the hover readout prints. */
  path: string;
}

/**
 * Lay the tree out left to right, heights ∝ A$.
 *
 * With `focus` set, that mine fills the chart: the total column becomes the
 * "back to all mines" control and only the mine's machines and drivers are
 * drawn, rescaled to the mine's value. Children stack top-down in the
 * engine's order (largest first), so the eye reads the biggest block first.
 */
export function icicleLayout(tree: DriverNode, focus: MineCode | null): IcicleCell[] {
  const total = tree.valueAud;
  const focused = focus ? tree.children.find((m) => m.mine === focus) ?? null : null;
  const mines = focused ? [focused] : tree.children;
  const base = focused ? focused.valueAud : total;
  if (!base) return [];

  const cells: IcicleCell[] = [
    {
      id: tree.id,
      column: 0,
      kind: 'root',
      label: focused ? 'All mines' : 'Total',
      valueAud: total,
      valueLabel: fmtk(total),
      shareOfTotal: 1,
      y: 0,
      h: 1,
      path: 'Total A$ exposure',
    },
  ];

  let mineY = 0;
  for (const mine of mines) {
    const mh = mine.valueAud / base;
    const mineName = mine.mine ? MSHORT[mine.mine] : mine.label;
    cells.push({
      id: mine.id,
      column: 1,
      kind: 'mine',
      label: mineName,
      sublabel: mine.label,
      valueAud: mine.valueAud,
      valueLabel: fmtk(mine.valueAud),
      shareOfTotal: mine.valueAud / total,
      y: mineY,
      h: mh,
      mine: mine.mine,
      path: mineName,
    });
    let machineY = mineY;
    for (const machine of mine.children) {
      const h = machine.valueAud / base;
      const component = machine.label.split(' · ').slice(1).join(' · ');
      cells.push({
        id: machine.id,
        column: 2,
        kind: 'machine',
        label: machine.machine ?? machine.label,
        sublabel: component,
        valueAud: machine.valueAud,
        valueLabel: fmtk(machine.valueAud),
        shareOfTotal: machine.valueAud / total,
        y: machineY,
        h,
        mine: mine.mine,
        machine: machine.machine,
        path: `${mineName} › ${machine.machine} · ${component}`,
      });
      let driverY = machineY;
      for (const d of machine.children) {
        const dh = d.valueAud / base;
        cells.push({
          id: d.id,
          column: 3,
          kind: 'driver',
          label: d.label,
          valueAud: d.valueAud,
          valueLabel: fmtk(d.valueAud),
          shareOfTotal: d.valueAud / total,
          y: driverY,
          h: dh,
          mine: mine.mine,
          machine: machine.machine,
          driverKind: d.driverKind,
          path: `${mineName} › ${machine.machine} › ${d.label} (${pct(d.share)} of the machine)`,
        });
        driverY += dh;
      }
      machineY += h;
    }
    mineY += mh;
  }
  return cells;
}

// ---------------------------------------------------------------------------
// Target first
// ---------------------------------------------------------------------------

export interface TargetRow {
  rank: number;
  machine: string;
  mine: MineCode;
  mineName: string;
  component: string;
  valueLabel: string;
  /** Exposure as a fraction of the largest row, for the inline bar. */
  bar: number;
  shareLabel: string;
  basisLabel: string;
  alertId?: string;
  alertHref?: string;
  machineHref: string;
  topDriver: DriverKind;
  topDriverLabel: string;
  /** The top driver's share of this machine's exposure. */
  topDriverShare: string;
  topDriverWhy: string;
  cause: RootCause;
  confidence: string;
  ownerRole: string;
  ownerName: string;
  ownerChannel: string;
  ownerSynthesised: boolean;
  /** What the Assign toast says. */
  assignMessage: string;
}

/** The ranked rows, shaped for the list. */
export function targetRows(view: PrioritiesView): TargetRow[] {
  const { tree, rows } = view;
  const max = rows[0]?.valueAud ?? 0;
  const topShare = new Map<string, number>();
  for (const mine of tree.children)
    for (const machine of mine.children)
      if (machine.machine && machine.children[0]) topShare.set(machine.machine, machine.children[0].share);

  return rows.map((r) => {
    const owner = r.owner;
    const ownerRole = owner?.role ?? 'Triage queue';
    const ownerName = owner?.personName ?? 'Unassigned';
    return {
      rank: r.rank,
      machine: r.machine,
      mine: r.mine,
      mineName: MSHORT[r.mine],
      component: r.component,
      valueLabel: fmt$(r.valueAud),
      bar: max ? r.valueAud / max : 0,
      shareLabel: tree.valueAud ? pct(r.valueAud / tree.valueAud) : '—',
      basisLabel: r.basis === 'alert' ? 'Open alert' : 'Risk-flagged',
      ...(r.alertId ? { alertId: r.alertId, alertHref: `/issues/alerts/${r.alertId}` } : {}),
      machineHref: `/machines/${r.machine}?tab=reliability`,
      topDriver: r.topDriver,
      topDriverLabel: r.topDriverLabel,
      topDriverShare: pct(topShare.get(r.machine) ?? 0),
      topDriverWhy: r.topDriverText,
      cause: r.cause,
      confidence: pct(r.confidence),
      ownerRole,
      ownerName,
      ownerChannel: owner?.channel ?? 'Triage queue',
      ownerSynthesised: owner?.synthesised ?? false,
      assignMessage: `${r.machine} ${r.component} assigned to ${ownerName} (${ownerRole}) · ${fmt$(r.valueAud)} at stake`,
    };
  });
}

// ---------------------------------------------------------------------------
// How exposure is computed — the source drawer
// ---------------------------------------------------------------------------

/** The calculation note, in words. */
export const EXPOSURE_NOTE =
  'Exposure = for a machine with an open alert, its cost of inaction (unplanned − planned: downtime h × lost-production rate + failure parts + labour × 1.6, less the planned job); for a machine with no alert but a predict_risk score above the 0.35 watch line, risk × the unplanned cost of a typical breakdown on its class. Each machine’s exposure is then split across its drivers by weight: predict_risk factor points, plus the root-cause operator signal, plus parts lead time when the part is not on the site shelf.';

/** The rows behind the total, with the rule and two worked examples. */
export function exposureSource(view: PrioritiesView, ctx: DriverTreeCtx): SourceTablePayload {
  const { tree, rows } = view;
  const steps: string[] = [];
  const firstAlert = rows.find((r) => r.basis === 'alert' && r.alertId);
  if (firstAlert?.alertId) {
    const alert = ALERTS.find((a) => a.id === firstAlert.alertId);
    const c = alert ? coi(alert) : null;
    if (c)
      steps.push(
        `${firstAlert.machine} (${firstAlert.alertId}): unplanned ${fmt$(c.unplanned)} − planned ${fmt$(c.planned)} = ${fmt$(c.delta)}`,
      );
  }
  const firstRisk = rows.find((r) => r.basis === 'risk');
  if (firstRisk && BY_ID[firstRisk.machine]) {
    const u = unplannedCost(firstRisk.machine, ctx);
    steps.push(
      `${firstRisk.machine} (risk-flagged): risk ${firstRisk.risk.toFixed(2)} × typical breakdown ${fmt$(u)} = ${fmt$(firstRisk.valueAud)}`,
    );
  }
  steps.push(
    `Σ over ${rows.length} machines (${rows.filter((r) => r.basis === 'alert').length} open alerts, ${rows.filter((r) => r.basis === 'risk').length} risk-flagged) = ${fmt$(tree.valueAud)}`,
  );
  return {
    kind: 'table',
    title: 'A$ exposure by machine',
    columns: ['Rank', 'Machine', 'Mine', 'Component', 'Basis', 'Alert', 'Risk', 'Exposure A$', 'Top driver', 'Root cause'],
    rows: rows.map((r) => [
      r.rank,
      r.machine,
      r.mine,
      r.component,
      r.basis === 'alert' ? 'Open alert' : 'Risk-flagged',
      r.alertId ?? '—',
      Number(r.risk.toFixed(2)),
      r.valueAud,
      r.topDriverLabel,
      CAUSE_PILL[r.cause].label,
    ]),
    provenance: `reliability engine · driver-tree.ts exposureByMachine() · ${rows.length} machines · alerts priced by coi(), risk from predict_risk, breakdowns from work_orders.csv`,
    highlight: rows.length ? [0] : [],
    calc: { formula: EXPOSURE_NOTE, steps },
  };
}
